import React, { useEffect, useState } from 'react'

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '')

// ── Token handshake ──────────────────────────────────────────────────
// GET /api/stats/users now requires a Bearer token, but this page runs inside
// an <iframe> (or a popup) owned by the BookTox admin panel on a *different*
// origin, so it cannot read that panel's localStorage.  We ask the embedder for
// a token over postMessage instead, and it answers with one.
//
// The request carries no secret, so it goes out with targetOrigin '*'.  The
// token only ever travels the other way, and we accept it only from an origin
// on this allowlist — never from an arbitrary page that framed us.
const ALLOWED_EMBEDDER_ORIGINS: string[] = String(
    import.meta.env.VITE_EMBEDDER_ORIGINS ??
    'https://innobooktox.com,https://www.innobooktox.com,http://localhost:5173'
)
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean)

const TOKEN_REQUEST = 'BOOKTOX_STATS_TOKEN_REQUEST'
const TOKEN_REPLY = 'BOOKTOX_STATS_TOKEN'
const TOKEN_TIMEOUT_MS = 4000

/** The window that embedded us: the parent frame, or the opener for a popup. */
function embedder(): Window | null {
  if (window.parent && window.parent !== window) return window.parent
  return window.opener ?? null
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface Segment {
  label: string
  count: number
}

interface StatsData {
  total_users: number
  age_distribution: Segment[]
  gender_distribution: Segment[]
}

// ── Constants ─────────────────────────────────────────────────────────────────

const AGE_ORDER = ['10대 이하', '20대', '30대', '40대', '50대', '60대 이상']
const AGE_COLORS = ['#6366f1', '#818cf8', '#38bdf8', '#34d399', '#fbbf24', '#f87171']
const GENDER_COLORS: Record<string, string> = {
  '남성': '#3b82f6',
  '여성': '#ec4899',
}

function colorFor(label: string, palette: string[], orderedLabels: string[]): string {
  const idx = orderedLabels.indexOf(label)
  return idx >= 0 ? palette[idx] : '#d1d5db'
}

// ── SVG Donut ─────────────────────────────────────────────────────────────────

interface DonutProps {
  segments: { color: string; value: number }[]
  total: number
  centerLabel: string
  centerSub: string
}

function DonutChart({ segments, total, centerLabel, centerSub }: DonutProps) {
  const R = 72
  const C = 2 * Math.PI * R
  let accumulated = 0

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 220, margin: '0 auto' }}>
      <svg viewBox="0 0 200 200" style={{ width: '100%', display: 'block' }}>
        {/* Background track */}
        <circle cx={100} cy={100} r={R} fill="none" stroke="#f0f2f5" strokeWidth={26} />
        {total === 0 ? (
          <circle cx={100} cy={100} r={R} fill="none" stroke="#e5e7eb" strokeWidth={26} />
        ) : (
          segments.filter(s => s.value > 0).map((seg, i) => {
            const dashLen = (seg.value / total) * C
            const rotateDeg = (accumulated / total) * 360 - 90
            accumulated += seg.value
            return (
              <circle
                key={i}
                cx={100} cy={100} r={R}
                fill="none"
                stroke={seg.color}
                strokeWidth={26}
                strokeDasharray={`${dashLen} ${C - dashLen}`}
                strokeDashoffset={0}
                transform={`rotate(${rotateDeg} 100 100)`}
              />
            )
          })
        )}
      </svg>
      {/* Center label */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e', lineHeight: 1 }}>
          {centerLabel}
        </span>
        <span style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{centerSub}</span>
      </div>
    </div>
  )
}

// ── Legend ────────────────────────────────────────────────────────────────────

function Legend({ segments, total }: { segments: (Segment & { color: string })[]; total: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
      {segments.map((seg) => {
        const pct = total > 0 ? ((seg.count / total) * 100).toFixed(1) : '0.0'
        return (
          <div key={seg.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 11, height: 11, borderRadius: '50%',
              background: seg.color, flexShrink: 0,
            }} />
            <span style={{ width: 68, fontSize: 13, color: '#555', flexShrink: 0 }}>
              {seg.label}
            </span>
            <div style={{
              flex: 1, background: '#f0f2f5', borderRadius: 99, height: 7, overflow: 'hidden',
            }}>
              <div style={{
                width: `${pct}%`, height: '100%',
                background: seg.color, borderRadius: 99,
                transition: 'width 0.6s ease',
              }} />
            </div>
            <div style={{ minWidth: 76, textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
                {pct}%
              </div>
              <div style={{ fontSize: 11, color: '#aaa', lineHeight: 1.2 }}>
                {seg.count.toLocaleString('ko-KR')}명
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Card ──────────────────────────────────────────────────────────────────────

function Card({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 20,
      padding: '32px 28px 28px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
    }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#aaa', marginBottom: 24 }}>{desc}</div>
      {children}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function StatisticsPage() {
  const [data, setData] = useState<StatsData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const host = embedder()
    if (!host) {
      setError('이 페이지는 독서노트 관리자 페이지에서 열어야 합니다.')
      setLoading(false)
      return
    }

    // Guards against a second reply (or the timeout) racing the first fetch.
    let settled = false

    const load = (token: string) => {
      if (settled) return
      settled = true
      fetch(`${API_BASE}/api/stats/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(async (r) => {
          const text = await r.text()
          if (r.status === 401 || r.status === 403) {
            throw new Error('조회 권한이 없거나 로그인이 만료되었습니다. 관리자 페이지를 새로고침해 주세요.')
          }
          let body: any
          try { body = JSON.parse(text) } catch {
            throw new Error(`서버 응답 오류 (HTTP ${r.status}) — 백엔드가 실행 중인지 확인하세요.`)
          }
          if (!r.ok) throw new Error(body?.error ?? r.statusText)
          setData(body)
        })
        .catch((e) => setError(String(e.message ?? e)))
        .finally(() => setLoading(false))
    }

    const onMessage = (e: MessageEvent) => {
      if (!ALLOWED_EMBEDDER_ORIGINS.includes(e.origin)) return
      if ((e.data as any)?.type !== TOKEN_REPLY) return
      const token = (e.data as any)?.token
      if (typeof token !== 'string' || !token) {
        if (settled) return
        settled = true
        setError('로그인 정보를 받지 못했습니다. 관리자 페이지에서 다시 로그인해 주세요.')
        setLoading(false)
        return
      }
      load(token)
    }

    // Listener first, then the request — otherwise a fast reply is lost.
    window.addEventListener('message', onMessage)
    host.postMessage({ type: TOKEN_REQUEST }, '*')

    // The embedder might not be listening (or might be an unrelated page that
    // framed us) — fail loudly instead of hanging on the loading state.
    const timer = window.setTimeout(() => {
      if (settled) return
      settled = true
      setError('관리자 페이지로부터 로그인 정보를 받지 못했습니다.')
      setLoading(false)
    }, TOKEN_TIMEOUT_MS)

    return () => {
      window.removeEventListener('message', onMessage)
      window.clearTimeout(timer)
    }
  }, [])

  // Normalize age segments: fill missing labels with 0, maintain order
  const ageFilled: (Segment & { color: string })[] = AGE_ORDER.map((label, i) => ({
    label,
    count: data?.age_distribution.find((d) => d.label === label)?.count ?? 0,
    color: AGE_COLORS[i],
  }))

  const genderFilled: (Segment & { color: string })[] = (data?.gender_distribution ?? []).map((d) => ({
    ...d,
    color: GENDER_COLORS[d.label] ?? '#a3a3a3',
  }))

  const total = data?.total_users ?? 0
  const ageTotal = ageFilled.reduce((s, d) => s + d.count, 0)
  const genderTotal = genderFilled.reduce((s, d) => s + d.count, 0)

  return (
    <div style={{
      fontFamily: "'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
      background: '#f0f2f5', minHeight: '100vh',
      padding: '40px 20px', color: '#1a1a2e',
    }}>
      <h1 style={{
        textAlign: 'center', fontSize: 28, fontWeight: 700,
        letterSpacing: '-0.5px', marginBottom: 8,
      }}>
        사용자 통계
      </h1>

      {/* Total users banner */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        background: '#fff', borderRadius: 16, padding: '18px 32px',
        maxWidth: 380, margin: '0 auto 12px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
      }}>
        <span style={{ fontSize: 28 }}>👥</span>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 26, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.1 }}>
            {loading ? '…' : error ? '—' : `${total.toLocaleString('ko-KR')}명`}
          </span>
          <span style={{ fontSize: 13, color: '#aaa', marginTop: 2 }}>전체 가입 사용자 수 (탈퇴 제외)</span>
        </div>
      </div>

      {/* Verification note */}
      {!loading && !error && data && (
        <p style={{ textAlign: 'center', fontSize: 12, color: '#bbb', marginBottom: 32 }}>
          Reading Notes & Page Box 2.0 함게 개산하는 회원 수
        </p>
      )}
      {(loading || error) && <div style={{ marginBottom: 36 }} />}

      {/* Error state */}
      {error && (
        <p style={{ textAlign: 'center', color: '#d00', fontSize: 14, marginBottom: 24 }}>
          데이터를 불러오지 못했습니다: {error}
        </p>
      )}

      {/* Loading skeleton */}
      {loading && (
        <p style={{ textAlign: 'center', color: '#aaa', fontSize: 14 }}>불러오는 중…</p>
      )}

      {/* Charts */}
      {!loading && !error && data && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 28,
          maxWidth: 900,
          margin: '0 auto',
        }}>
          {/* Age distribution */}
          <Card title="연령층 분포" desc="사용자 연령별 비율 및 인원 수 (본인인증 기준)">
            <DonutChart
              segments={ageFilled.map((d) => ({ color: d.color, value: d.count }))}
              total={ageTotal}
              centerLabel={ageTotal > 0 ? `${ageTotal.toLocaleString('ko-KR')}명` : '—'}
              centerSub="인증 완료"
            />
            <Legend segments={ageFilled} total={ageTotal} />
          </Card>

          {/* Gender distribution */}
          <Card title="성별 분포" desc="사용자 성별 비율 및 인원 수 (본인인증 기준)">
            <DonutChart
              segments={genderFilled.map((d) => ({ color: d.color, value: d.count }))}
              total={genderTotal}
              centerLabel={genderTotal > 0 ? `${genderTotal.toLocaleString('ko-KR')}명` : '—'}
              centerSub="인증 완료"
            />
            <Legend segments={genderFilled} total={genderTotal} />
          </Card>
        </div>
      )}

      <p style={{ textAlign: 'center', fontSize: 12, color: '#bbb', marginTop: 36 }}>
        ※ 연령·성별 통계는 본인인증 완료 사용자 기준입니다.
      </p>
    </div>
  )
}